/**
 * useBuildVersionChecker — proactive deployment detection for Capacitor apps.
 *
 * The Capacitor app loads from a remote Next.js server. When the server deploys
 * new code, the old chunk hashes referenced by the running app become invalid,
 * causing ChunkLoadError. This hook polls /api/v1/build-info and proactively
 * reloads the page when a new deployment is detected — before any chunk
 * errors occur.
 *
 * Polling interval: 3 minutes (configurable).
 * Also checks on app resume (from background) via Capacitor App plugin.
 *
 * CANARY SAFETY: Requires CONSECUTIVE_CONFIRMATIONS (2) consecutive polls
 * returning the same different commit before reloading. This prevents
 * reload loops during canary deployments where HAProxy routes ~10% of
 * traffic to the canary slot (which has a different git commit). A single
 * canary hit won't trigger a reload, but after the canary is promoted and
 * 100% of traffic is on the new commit, both polls will match and trigger
 * correctly.
 *
 * Only active on Capacitor native platforms. No-op on web/desktop.
 */

'use client';

import { useEffect, useRef } from 'react';
import { isCapacitor } from '@/lib/platformUtils';

const BUILD_INFO_ENDPOINT = '/api/v1/build-info';
const DEFAULT_POLL_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
const RESUME_DEBOUNCE_MS = 2000;
const CONSECUTIVE_CONFIRMATIONS = 2;

interface BuildInfo {
  git_commit?: string;
  git_commit_short?: string;
  build_timestamp?: string;
}

export function useBuildVersionChecker(
  pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS,
): void {
  const initialCommitRef = useRef<string | null>(null);
  const candidateCommitRef = useRef<string | null>(null);
  const confirmationsRef = useRef(0);
  const isCheckingRef = useRef(false);

  useEffect(() => {
    if (!isCapacitor()) return;

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const clearCachesAndReload = async (newCommit: string) => {
      console.log(
        '[BuildVersionChecker] New deployment confirmed!',
        `${initialCommitRef.current?.slice(0, 8)} → ${newCommit.slice(0, 8)}`,
      );

      // Clear caches before reloading to ensure fresh chunks
      try {
        if (navigator.serviceWorker?.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHES' });
        }
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames
              .filter(name =>
                name.includes('nextjs') ||
                name.includes('totalaccess') ||
                name.includes('pages') ||
                name.includes('navigation') ||
                name.includes('chunks')
              )
              .map(name => caches.delete(name))
          );
        }
      } catch (e) {
        console.warn('[BuildVersionChecker] Cache clear failed:', e);
      }

      // Reload to get fresh HTML + chunks from the new deployment
      window.location.reload();
    };

    const checkBuildVersion = async (isResume = false) => {
      if (cancelled || isCheckingRef.current) return;
      isCheckingRef.current = true;

      try {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timeoutId = controller
          ? setTimeout(() => controller.abort(), 8000)
          : null;

        let response: Response;
        try {
          response = await fetch(BUILD_INFO_ENDPOINT, {
            cache: 'no-store',
            signal: controller?.signal,
          });
        } finally {
          if (timeoutId) clearTimeout(timeoutId);
        }

        if (!response.ok) return;

        const data: BuildInfo = await response.json();
        const currentCommit = data.git_commit;

        if (!currentCommit) return;

        // Store the initial commit on first check
        if (initialCommitRef.current === null) {
          initialCommitRef.current = currentCommit;
          if (!isResume) {
            console.log('[BuildVersionChecker] Initial build commit:', data.git_commit_short || currentCommit.slice(0, 8));
          }
          return;
        }

        // Same commit as initial — no deployment change. Clear any candidate.
        if (initialCommitRef.current === currentCommit) {
          if (candidateCommitRef.current !== null) {
            candidateCommitRef.current = null;
            confirmationsRef.current = 0;
          }
          return;
        }

        // Different commit from initial — potential new deployment.
        // Require CONSECUTIVE_CONFIRMATIONS consecutive polls with the same
        // different commit before reloading. This prevents reload loops
        // during canary deployments where HAProxy routes a fraction of
        // traffic to a canary slot with a different git commit.
        if (candidateCommitRef.current === currentCommit) {
          confirmationsRef.current += 1;
          console.log(
            '[BuildVersionChecker] Deployment candidate confirmed:',
            `${confirmationsRef.current}/${CONSECUTIVE_CONFIRMATIONS}`,
            `(${currentCommit.slice(0, 8)})`,
          );
        } else {
          candidateCommitRef.current = currentCommit;
          confirmationsRef.current = 1;
          console.log(
            '[BuildVersionChecker] New deployment candidate detected:',
            `${initialCommitRef.current.slice(0, 8)} → ${currentCommit.slice(0, 8)}`,
            `(waiting for ${CONSECUTIVE_CONFIRMATIONS} consecutive confirmations)`,
          );
        }

        if (confirmationsRef.current >= CONSECUTIVE_CONFIRMATIONS) {
          await clearCachesAndReload(currentCommit);
        }
      } catch (e) {
        // Network errors are expected when offline — silently skip
        if (e instanceof Error && e.name !== 'AbortError') {
          console.debug('[BuildVersionChecker] Check failed:', e.message);
        }
      } finally {
        isCheckingRef.current = false;
      }
    };

    const scheduleNext = () => {
      if (cancelled) return;
      pollTimer = setTimeout(async () => {
        await checkBuildVersion();
        scheduleNext();
      }, pollIntervalMs);
    };

    // Initial check after a short delay (don't block app startup)
    const initialTimer = setTimeout(() => checkBuildVersion(), 5000);

    // Start polling
    scheduleNext();

    // Also check on app resume (from background)
    let resumeUnsubscribe: (() => void) | null = null;
    let resumeDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    (async () => {
      try {
        const { App: CapacitorApp } = await import('@capacitor/app');
        const listener = await CapacitorApp.addListener('resume', () => {
          // Debounce resume events (Android can fire multiple)
          if (resumeDebounceTimer) clearTimeout(resumeDebounceTimer);
          resumeDebounceTimer = setTimeout(() => {
            console.log('[BuildVersionChecker] App resumed — checking for new deployment');
            void checkBuildVersion(true);
          }, RESUME_DEBOUNCE_MS);
        });
        resumeUnsubscribe = () => {
          void listener.remove();
        };
      } catch {
        // Capacitor App plugin not available — skip resume detection
      }
    })();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (initialTimer) clearTimeout(initialTimer);
      if (resumeDebounceTimer) clearTimeout(resumeDebounceTimer);
      if (resumeUnsubscribe) resumeUnsubscribe();
    };
  }, [pollIntervalMs]);
}
